import { useEffect, useState } from "react";
import api, { rupiah, getErrorMessage, downloadFile } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, GitCompare, AlertTriangle } from "lucide-react";

const ST_STYLE = { "Confirmed Related Party": "bg-red-100 text-red-700 border-red-300", "Not Related": "bg-slate-100 text-slate-500 border-slate-300", "Further Investigation": "bg-amber-100 text-amber-800 border-amber-300", "Pending": "bg-blue-100 text-blue-700 border-blue-300" };

export default function RelatedParty({ id }) {
  return (
    <Tabs defaultValue="parties">
      <TabsList className="mb-3">
        <TabsTrigger value="parties" data-testid="rp-tab-parties">Daftar Pihak Berelasi</TabsTrigger>
        <TabsTrigger value="txns" data-testid="rp-tab-txns">Transaksi Pihak Berelasi</TabsTrigger>
      </TabsList>
      <TabsContent value="parties"><Parties id={id} /></TabsContent>
      <TabsContent value="txns"><Txns id={id} /></TabsContent>
    </Tabs>
  );
}

function Parties({ id }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const load = () => api.get(`/engagements/${id}/related-parties`).then((r) => setRows(r.data));
  useEffect(() => { load(); api.get(`/meta/related-party`).then((r) => setMeta(r.data)); }, [id]);

  const crossMatch = async () => { try { const { data } = await api.post(`/engagements/${id}/related-parties/cross-match`); toast.success(`${data.suggested} kandidat baru dari ${data.scanned_datasets} dataset / ${data.scanned_rows} baris`); load(); } catch (e) { toast.error(getErrorMessage(e)); } };
  const addDeclared = async () => { const name = window.prompt("Nama pihak berelasi (dideklarasikan klien):"); if (!name) return; await api.post(`/engagements/${id}/related-parties`, { name, source: "declared" }); load(); };
  // A party the auditor identifies (e.g. from the directors' list) is not one the client declared (B-450).
  const addIdentified = async () => {
    const name = window.prompt("Nama pihak berelasi yang diidentifikasi auditor:"); if (!name) return;
    const basis = window.prompt("Sumber identifikasi (wajib) — mis. daftar pengurus, akta, notulen:"); if (!basis || !basis.trim()) return;
    try { await api.post(`/engagements/${id}/related-parties`, { name, source: "identified", director_relationship: basis }); load(); toast.success("Pihak dicatat; tentukan statusnya"); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const review = async (p, status) => {
    let rationale = p.rationale;
    if (!rationale) { rationale = window.prompt("Rasional keputusan auditor (wajib):"); if (!rationale) return; }
    try { await api.put(`/related-parties/${p.id}`, { status, rationale }); load(); toast.success("Tersimpan"); } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const declared = rows.filter((r) => r.source === "declared");
  const suggested = rows.filter((r) => r.source === "suggested");
  const identified = rows.filter((r) => r.source === "identified");

  const Table = ({ list, testid }) => (
    <div className="bg-white border border-border rounded-sm overflow-x-auto" data-testid={testid}>
      <table className="w-full audit-table">
        <thead><tr><th>Party ID</th><th>Nama</th><th>Hubungan</th><th>Indikator / Catatan</th><th>Status</th><th>Aksi Auditor</th></tr></thead>
        <tbody>
          {list.map((p) => (
            <tr key={p.id} data-testid={`rp-row-${p.id}`}>
              <td className="font-mono text-[10px]">{p.party_id}</td>
              <td className="text-xs font-medium">{p.name}{p.flag && <div className="text-[9px] text-red-600 flex items-center gap-1"><AlertTriangle size={10} />{p.flag}</div>}</td>
              <td className="text-xs">{p.relationship_type || "—"}</td>
              <td className="text-[10px] text-muted-foreground">{(p.match_indicators || []).join("; ") || p.director_relationship || p.rationale || "—"}</td>
              <td><span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${ST_STYLE[p.status] || ""}`}>{p.status}</span></td>
              <td className="flex gap-1">
                <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-600" onClick={() => review(p, "Confirmed Related Party")} data-testid={`rp-confirm-${p.id}`}>Terkait</Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => review(p, "Not Related")} data-testid={`rp-not-${p.id}`}>Tidak</Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px] text-amber-700" onClick={() => review(p, "Further Investigation")} data-testid={`rp-invest-${p.id}`}>Investigasi</Button>
              </td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-3">Kosong.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={crossMatch} data-testid="rp-cross-match"><GitCompare size={13} className="mr-1" />Cross-Match Dataset</Button>
        <Button size="sm" variant="outline" onClick={addDeclared} data-testid="rp-add-declared"><Plus size={13} className="mr-1" />Pihak Dideklarasikan</Button>
        <Button size="sm" variant="outline" onClick={addIdentified} data-testid="rp-add-identified"><Plus size={13} className="mr-1" />Pihak Diidentifikasi Auditor</Button>
        <Button size="sm" variant="outline" onClick={() => downloadFile(`/engagements/${id}/export/related-party`, "Pihak_Berelasi.xlsx")} data-testid="rp-export"><GitCompare size={13} className="mr-1" />Ekspor KKA Pihak Berelasi</Button>
      </div>
      <div className="text-xs font-head font-semibold">Dideklarasikan Klien ({declared.length})</div>
      <Table list={declared} testid="rp-declared" />
      <div className="text-xs font-head font-semibold">Diidentifikasi Auditor — tidak dideklarasikan klien ({identified.length})</div>
      <Table list={identified} testid="rp-identified" />
      <div className="text-xs font-head font-semibold text-amber-800">Potensial (Sistem) — Perlu Validasi Auditor ({suggested.length})</div>
      <Table list={suggested} testid="rp-suggested" />
      <p className="text-[11px] text-muted-foreground" data-testid="rp-match-basis">Cross-match hanya menandai nama, rekening, NPWP, telepon atau e-mail yang sama pada dataset dari sisi berbeda (pelanggan, pemasok, karyawan, pengurus). Pihak yang sama di dua daftar sisi yang sama bukan indikator.</p>
    </div>
  );
}

function Txns({ id }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const load = () => api.get(`/engagements/${id}/related-party-transactions`).then((r) => setRows(r.data));
  useEffect(() => { load(); api.get(`/meta/related-party`).then((r) => setMeta(r.data)); }, [id]);
  const add = async () => { const cp = window.prompt("Counterparty transaksi:"); if (!cp) return; await api.post(`/engagements/${id}/related-party-transactions`, { counterparty: cp }); load(); };
  const upd = async (t, patch) => { await api.put(`/related-party-transactions/${t.id}`, patch); load(); };

  return (
    <div className="space-y-2">
      <Button size="sm" onClick={add} data-testid="rp-txn-add"><Plus size={13} className="mr-1" />Tambah Transaksi</Button>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full audit-table">
          <thead><tr><th>Counterparty</th><th>Sifat</th><th>Akun</th><th>Jumlah</th><th>Syarat</th><th>Pengungkapan</th><th>Penilaian Auditor</th></tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} data-testid={`rp-txn-${t.id}`}>
                <td className="text-xs font-medium">{t.counterparty}</td>
                <td><Input className="h-7 text-xs w-28" defaultValue={t.nature} onBlur={(e) => e.target.value !== t.nature && upd(t, { nature: e.target.value })} /></td>
                <td><Input className="h-7 text-xs w-24" defaultValue={t.account} onBlur={(e) => e.target.value !== t.account && upd(t, { account: e.target.value })} /></td>
                <td><Input className="h-7 text-xs w-28" defaultValue={t.amount} onBlur={(e) => e.target.value !== t.amount && upd(t, { amount: e.target.value })} /></td>
                <td>{meta && <Select value={t.market_terms || ""} onValueChange={(v) => upd(t, { market_terms: v })}><SelectTrigger className="h-7 text-xs w-28"><SelectValue placeholder="—" /></SelectTrigger><SelectContent><SelectItem value="Wajar">Wajar</SelectItem><SelectItem value="Tidak Wajar">Tidak Wajar</SelectItem></SelectContent></Select>}</td>
                <td>{meta && <Select value={t.disclosure_status || ""} onValueChange={(v) => upd(t, { disclosure_status: v })}><SelectTrigger className="h-7 text-xs w-32"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{meta.disclosure.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>}</td>
                <td><Input className="h-7 text-xs w-40" defaultValue={t.auditor_assessment} onBlur={(e) => e.target.value !== t.auditor_assessment && upd(t, { auditor_assessment: e.target.value })} data-testid={`rp-txn-assess-${t.id}`} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-3">Belum ada transaksi.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
